import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchStrategies, fetchNearbyStrikes, manualEntry, manualExit, fetchStrategy } from '../api';

const REFRESH_INTERVAL = 5000;

export default function ManualTrade() {
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [strategyDetail, setStrategyDetail] = useState(null);
  const navigate = useNavigate();

  // Entry form
  const [index, setIndex] = useState('Nifty');
  const [strikes, setStrikes] = useState([]);
  const [spotPrice, setSpotPrice] = useState(0);
  const [atmStrike, setAtmStrike] = useState(0);
  const [selectedStrike, setSelectedStrike] = useState('');
  const [customStrike, setCustomStrike] = useState('');
  const [optionType, setOptionType] = useState('CE');
  const [side, setSide] = useState('SELL');
  const [lots, setLots] = useState(1);
  const [slType, setSlType] = useState('none');
  const [slValue, setSlValue] = useState('');

  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadStrategies = useCallback(async () => {
    try {
      const res = await fetchStrategies();
      const manualStrats = (res.data?.strategies || [])
        .filter(s => s.entry_type === 'manual_single_leg');
      setStrategies(manualStrats);
      if (!selectedStrategy && manualStrats.length > 0) {
        setSelectedStrategy(manualStrats[0].name);
      }
    } catch {}
  }, [selectedStrategy]);

  const loadStrikes = useCallback(async () => {
    try {
      const res = await fetchNearbyStrikes(index, 10);
      if (res.data?.status === 'ok') {
        setStrikes(res.data.strikes || []);
        setSpotPrice(res.data.spot_price || 0);
        setAtmStrike(res.data.atm_strike || 0);
      }
    } catch {}
  }, [index]);

  const loadDetail = useCallback(async () => {
    if (!selectedStrategy) return;
    try {
      const res = await fetchStrategy(selectedStrategy);
      if (res.data?.strategy) setStrategyDetail(res.data.strategy);
    } catch {}
  }, [selectedStrategy]);

  useEffect(() => { loadStrategies(); }, [loadStrategies]);
  useEffect(() => { loadStrikes(); }, [loadStrikes]);
  useEffect(() => {
    loadDetail();
    const id = setInterval(() => { loadDetail(); loadStrikes(); }, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [loadDetail, loadStrikes]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  const getActiveStrike = () => {
    if (customStrike) return parseFloat(customStrike);
    if (selectedStrike) return parseFloat(selectedStrike);
    return 0;
  };

  const handleEntry = async () => {
    const strike = getActiveStrike();
    if (!strike || !selectedStrategy) {
      setMessage({ type: 'error', text: 'Select a strategy and strike price' });
      return;
    }
    setLoading(true);
    setConfirmOpen(false);
    try {
      const res = await manualEntry({
        strategy_name: selectedStrategy,
        strike, option_type: optionType, side, lots,
        sl_type: slType, sl_value: slValue ? parseFloat(slValue) : 0,
      });
      setMessage({ type: res.data?.status === 'ok' ? 'success' : 'error', text: res.data?.message });
      if (res.data?.status === 'ok') {
        setCustomStrike(''); setSelectedStrike('');
        setTimeout(loadDetail, 1000);
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Entry failed' });
    }
    setLoading(false);
  };

  const handleExit = async (posIndex = -1) => {
    if (!selectedStrategy) return;
    setLoading(true);
    try {
      const res = await manualExit({ strategy_name: selectedStrategy, position_index: posIndex });
      setMessage({ type: res.data?.status === 'ok' ? 'success' : 'error', text: res.data?.message });
      setTimeout(loadDetail, 1000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Exit failed' });
    }
    setLoading(false);
  };

  const openPositions = strategyDetail?.positions || [];
  const events = strategyDetail?.events || [];
  const pnl = strategyDetail?.pnl || 0;
  const activeStrike = getActiveStrike();
  const currentStrat = strategies.find(s => s.name === selectedStrategy);

  if (strategies.length === 0) {
    return (
      <div className="mt-page">
        <div className="mt-empty">
          <div className="mt-empty-icon">🎯</div>
          <h2>Manual Trading</h2>
          <p>Place single-leg option trades with custom strike prices and stop loss.</p>
          <div className="mt-empty-steps">
            <div className="mt-step"><span className="mt-step-num">1</span><span>Go to <strong>Settings</strong></span></div>
            <div className="mt-step"><span className="mt-step-num">2</span><span>Click <strong>+ Add Strategy</strong></span></div>
            <div className="mt-step"><span className="mt-step-num">3</span><span>Select type <strong>Manual Trade</strong></span></div>
          </div>
          <button className="mt-btn mt-btn-primary" onClick={() => navigate('/settings')}>Go to Settings</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-page">
      {message && (
        <div className={`mt-toast mt-toast-${message.type}`} onClick={() => setMessage(null)}>
          <span className="mt-toast-icon">{message.type === 'success' ? '✓' : '✕'}</span>
          {message.text}
        </div>
      )}

      <div className="mt-header">
        <div className="mt-header-left">
          <button className="sd-back" onClick={() => navigate('/strategies')}>← Strategies</button>
          <h1>Manual Trade</h1>
          <div className="mt-index-toggle">
            {['Nifty', 'Sensex'].map(i => (
              <button key={i} className={`mt-idx-btn ${index === i ? 'mt-idx-active' : ''}`}
                onClick={() => setIndex(i)}>{i}</button>
            ))}
          </div>
        </div>
        <div className="mt-header-right">
          {spotPrice > 0 && (
            <div className="mt-spot-display">
              <span className="mt-spot-label">{index} Spot</span>
              <span className="mt-spot-value">{spotPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          )}
          <div className="mt-strategy-selector">
            <select value={selectedStrategy} onChange={e => setSelectedStrategy(e.target.value)}>
              {strategies.map(s => (
                <option key={s.name} value={s.name}>
                  {s.account_name || 'Paper'} {s.paper_trading ? '(Paper)' : '(Live)'}
                </option>
              ))}
            </select>
            {currentStrat && (
              <span className={`mt-mode-badge ${currentStrat.paper_trading ? '' : 'mt-mode-live'}`}>
                {currentStrat.paper_trading ? 'PAPER' : 'LIVE'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-layout">
        {/* Left: Order Entry */}
        <div className="mt-order-panel">
          <div className="mt-side-toggle">
            <button className={`mt-side-btn mt-side-sell ${side === 'SELL' ? 'mt-side-active' : ''}`}
              onClick={() => setSide('SELL')}>
              <span className="mt-side-label">SELL</span>
              <span className="mt-side-sub">Short / Write</span>
            </button>
            <button className={`mt-side-btn mt-side-buy ${side === 'BUY' ? 'mt-side-active' : ''}`}
              onClick={() => setSide('BUY')}>
              <span className="mt-side-label">BUY</span>
              <span className="mt-side-sub">Long / Buy</span>
            </button>
          </div>

          <div className="mt-option-toggle">
            <button className={`mt-opt-btn ${optionType === 'CE' ? 'mt-opt-ce-active' : ''}`}
              onClick={() => setOptionType('CE')}>
              <span className="mt-opt-icon">▲</span> CE <span className="mt-opt-hint">Call</span>
            </button>
            <button className={`mt-opt-btn ${optionType === 'PE' ? 'mt-opt-pe-active' : ''}`}
              onClick={() => setOptionType('PE')}>
              <span className="mt-opt-icon">▼</span> PE <span className="mt-opt-hint">Put</span>
            </button>
          </div>

          <div className="mt-strike-section">
            <div className="mt-section-head">
              <label>Strike Price</label>
              {activeStrike > 0 && <span className="mt-selected-strike">{activeStrike}</span>}
            </div>
            <div className="mt-strike-chain">
              {strikes.map(s => {
                const isAtm = s.label === 'ATM';
                const isSelected = selectedStrike === String(s.strike);
                const isOtm = (optionType === 'CE' ? s.diff > 0 : s.diff < 0);
                const isItm = (optionType === 'CE' ? s.diff < 0 : s.diff > 0);
                return (
                  <button key={s.strike}
                    className={`mt-strike-row ${isAtm ? 'mt-strike-atm' : ''} ${isSelected ? 'mt-strike-sel' : ''} ${isItm ? 'mt-strike-itm' : ''} ${isOtm ? 'mt-strike-otm' : ''}`}
                    onClick={() => { setSelectedStrike(String(s.strike)); setCustomStrike(''); }}>
                    <span className="mt-str-val">{s.strike.toLocaleString()}</span>
                    <span className="mt-str-diff">{s.label}</span>
                    {isAtm && <span className="mt-str-tag">ATM</span>}
                    {isItm && !isAtm && <span className="mt-str-tag mt-tag-itm">ITM</span>}
                    {isOtm && !isAtm && <span className="mt-str-tag mt-tag-otm">OTM</span>}
                  </button>
                );
              })}
            </div>
            <div className="mt-custom-strike">
              <input type="number" placeholder="Custom strike price..."
                value={customStrike}
                onChange={e => { setCustomStrike(e.target.value); setSelectedStrike(''); }} />
            </div>
          </div>

          <div className="mt-lots-row">
            <label>Lots</label>
            <div className="mt-lots-control">
              <button className="mt-lots-btn" onClick={() => setLots(Math.max(1, lots - 1))}>−</button>
              <input type="number" min="1" value={lots}
                onChange={e => setLots(Math.max(1, parseInt(e.target.value) || 1))} />
              <button className="mt-lots-btn" onClick={() => setLots(lots + 1)}>+</button>
            </div>
          </div>

          <div className="mt-sl-section">
            <label>Stop Loss</label>
            <div className="mt-sl-types">
              {[['none', 'None'], ['spot', 'Spot Price'], ['option', 'Option Price']].map(([val, label]) => (
                <button key={val} className={`mt-sl-btn ${slType === val ? 'mt-sl-active' : ''}`}
                  onClick={() => setSlType(val)}>{label}</button>
              ))}
            </div>
            {slType !== 'none' && (
              <div className="mt-sl-input">
                <span className="mt-sl-hint">
                  {slType === 'spot'
                    ? (side === 'SELL'
                      ? (optionType === 'CE' ? `Exit if ${index} ≥` : `Exit if ${index} ≤`)
                      : (optionType === 'CE' ? `Exit if ${index} ≤` : `Exit if ${index} ≥`))
                    : (side === 'SELL' ? 'Exit if premium ≥' : 'Exit if premium ≤')}
                </span>
                <input type="number" step="0.05"
                  placeholder={slType === 'spot' ? Math.round(spotPrice || 22500) : '150'}
                  value={slValue} onChange={e => setSlValue(e.target.value)} />
              </div>
            )}
          </div>

          <div className="mt-order-summary">
            <div className="mt-summary-row">
              <span className={`mt-sum-side ${side === 'SELL' ? 'mt-sum-sell' : 'mt-sum-buy'}`}>{side}</span>
              <span className={`mt-sum-opt ${optionType === 'CE' ? 'mt-sum-ce' : 'mt-sum-pe'}`}>{optionType}</span>
              <span className="mt-sum-strike">{activeStrike > 0 ? activeStrike.toLocaleString() : '—'}</span>
              <span className="mt-sum-lots">× {lots}</span>
            </div>
            {slType !== 'none' && slValue && (
              <div className="mt-sum-sl">
                SL: {slType === 'spot' ? `${index} @ ${parseFloat(slValue).toLocaleString()}` : `Premium ≥ ${slValue}`}
              </div>
            )}
          </div>

          {!confirmOpen ? (
            <button
              className={`mt-submit-btn ${side === 'SELL' ? 'mt-submit-sell' : 'mt-submit-buy'}`}
              disabled={loading || !activeStrike}
              onClick={() => setConfirmOpen(true)}>
              {side === 'SELL' ? '↓' : '↑'} Place {side} Order
            </button>
          ) : (
            <div className="mt-confirm">
              <p>Confirm: <strong>{side} {optionType} {activeStrike} × {lots}</strong>?</p>
              <div className="mt-confirm-btns">
                <button className={`mt-btn ${side === 'SELL' ? 'mt-btn-sell' : 'mt-btn-buy'}`}
                  disabled={loading} onClick={handleEntry}>
                  {loading ? 'Placing...' : 'Yes, Place Order'}
                </button>
                <button className="mt-btn mt-btn-ghost" onClick={() => setConfirmOpen(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Positions & Events */}
        <div className="mt-right-panel">
          <div className="mt-pnl-card">
            <div className="mt-pnl-main">
              <span className="mt-pnl-label">Total P&L</span>
              <span className={`mt-pnl-value ${pnl >= 0 ? 'mt-pnl-pos' : 'mt-pnl-neg'}`}>
                {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
                <small>pts/lot</small>
              </span>
            </div>
            <div className="mt-pnl-stats">
              <div className="mt-pnl-stat">
                <span className="mt-stat-label">State</span>
                <span className={`mt-stat-value mt-state-${(strategyDetail?.state || 'idle').toLowerCase()}`}>
                  {strategyDetail?.state || 'IDLE'}
                </span>
              </div>
              <div className="mt-pnl-stat">
                <span className="mt-stat-label">Open</span>
                <span className="mt-stat-value">{openPositions.length}</span>
              </div>
              <div className="mt-pnl-stat">
                <span className="mt-stat-label">Closed</span>
                <span className="mt-stat-value">{strategyDetail?.params?.closed_trades || 0}</span>
              </div>
            </div>
          </div>

          <div className="mt-card">
            <div className="mt-card-head">
              <h3>Open Positions</h3>
              {openPositions.length > 0 && (
                <button className="mt-btn mt-btn-danger-sm" onClick={() => handleExit(-1)} disabled={loading}>
                  Exit All
                </button>
              )}
            </div>
            {openPositions.length === 0 ? (
              <div className="mt-empty-pos">
                <span className="mt-empty-pos-icon">📭</span>
                <p>No open positions</p>
              </div>
            ) : (
              <div className="mt-positions">
                {openPositions.map((pos, idx) => (
                  <div key={idx} className="mt-pos-card">
                    <div className="mt-pos-top">
                      <div className="mt-pos-badges">
                        <span className={`mt-badge ${pos.side === 'SELL' ? 'mt-badge-sell' : 'mt-badge-buy'}`}>
                          {pos.side}
                        </span>
                        <span className={`mt-badge ${pos.option_type === 'CE' ? 'mt-badge-ce' : 'mt-badge-pe'}`}>
                          {pos.option_type}
                        </span>
                      </div>
                      <span className="mt-pos-strike">{pos.strike?.toLocaleString()}</span>
                      <span className="mt-pos-lots">×{pos.lots}</span>
                      <span className={`mt-pos-pnl ${pos.pnl >= 0 ? 'mt-pnl-pos' : 'mt-pnl-neg'}`}>
                        {pos.pnl >= 0 ? '+' : ''}{pos.pnl?.toFixed(2)}
                      </span>
                    </div>
                    <div className="mt-pos-bottom">
                      <div className="mt-pos-prices">
                        <span>Entry <strong>{pos.entry_price?.toFixed(2) || '—'}</strong></span>
                        <span className="mt-pos-arrow">→</span>
                        <span>LTP <strong>{pos.current_price?.toFixed(2) || '—'}</strong></span>
                      </div>
                      <button className="mt-btn mt-btn-exit" onClick={() => handleExit(idx)} disabled={loading}>
                        Exit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-card">
            <div className="mt-card-head">
              <h3>Event Log</h3>
              <span className="mt-event-count">{events.length}</span>
            </div>
            <div className="mt-events">
              {events.length === 0 ? (
                <p className="mt-muted">No events yet</p>
              ) : (
                events.slice().reverse().slice(0, 20).map((evt, idx) => (
                  <div key={idx} className="mt-event">
                    <span className="mt-evt-time">{evt.time}</span>
                    <span className={`mt-evt-type mt-evt-${evt.type?.toLowerCase().replace(/_/g, '')}`}>{evt.type}</span>
                    <span className="mt-evt-msg">{evt.message}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
